import { ReservationDriverDetailsModel } from './reservation-drivers/reservation-drivers.model';
import { ReservationValueDetailsModel } from './reservation-values/reservation-values.model';

export interface ReservationCalculatedDataQueryModel {
  brokerClientId: number;
  vehicleCategoryUid?: string | null;
  pickupLocationUid: string;
  dateOut: string;
  dropOffLocationUid: string;
  dateIn: string;
}

export interface CalculatedVehicleCategoryModel {
  uid: string;
  code: string;
  name: string;
  availableExtras: ReservationValueDetailsModel[];
}

export interface ReservationCalculatedDataModel {
  vehicleCategories: CalculatedVehicleCategoryModel[];
}

export interface ReservationListModel {
  uid: string;
  number: string;
  rentalAgreementNumber: string;
  referenceNumber: string;
  driverFirstName: string;
  driverLastName: string;
  locationOut: string;
  locationIn: string;
  dateOut: string;
  dateIn: string;
  clientNumber: string;
  reservationCreatedDate: string;
}

export interface ReservationDetailsModel {
  uid: string;
  number: string;
  status: ReservationStatus;
  version: number;
  reservationType: ReservationType;
  dateOut: string;
  dateIn: string;
  pickupLocationUid: string;
  dropOffLocationUid: string;
  vehicleCategoryUid: string;
  servedVehicleCategoryUid: string | null;
  brokerClientId: number;
  numberOfDaysToInvoice: number;
  numberOfDaysToInvoiceCashier: number;
  numberOfDays: number;
  brokerReference: string;
  rate: string;
  ratePeriodId: number;
  observations: string;
  currency: string;
  taxRate: number;
  totalTaxValue: number;
  cdwExcess: number;
  tpExcess: number;
  areKmsIncluded: boolean;
  areKmsFreePerDay: boolean;
  freeKmsPerDay: number;
  additionalKmsPrice: number;
  discountAmount: number;
  brokerOrigin: string;
  mainDriver: ReservationDriverDetailsModel | null;
  totalCashier: number;
  totalCashierWithTax: number;
  totalInvoice: number;
  totalInvoiceWithTax: number;
  totalValue: number;
  totalValueWithTax: number;
  rentalAgreementUid: string | null;
  rentalAgreementNumber: string | null;
  reservationCreatedDate: string;
  createdBy: string;
  reservationModifiedDate: string;
  modifiedBy: string | null;
  drivers: ReservationDriverDetailsModel[];
  valueItems: ReservationValueDetailsModel[];
}

export enum ReservationStatus {
  Confirmed = 0,
  NoShow = 1,
  Cancelled = 2,
  Pending = 3,
  NotConfirmed = 4
}

export const ReservationStatusesDisplayNames = ['Confirmed', 'No Show', 'Cancelled', 'Pending', 'Not Confirmed'];

export enum ReservationType {
  PP = 0,
  POA = 1
}

export interface ReservationUpdateModel {
  uid: string;
  status: ReservationStatus;
  servedVehicleCategoryUid: string | null;
  observations: string;
  dateIn: string;
  // Accepted extras. Omitted when the current selection hasn't been loaded yet, so a save that
  // doesn't touch extras leaves them untouched on the server.
  valueItems?: ReservationFullCreateValueItemModel[];
  // Full driver list. Omitted when the current drivers haven't been loaded yet, so a save that
  // doesn't touch them leaves them untouched on the server.
  drivers?: ReservationDriverDetailsModel[];
}

export interface ReservationCreateModel {
  dateOut: string;
  dateIn: string;
  pickupLocationUid: string;
  dropOffLocationUid: string;
  vehicleCategoryUid: string;
  servedVehicleCategoryUid: string | null;
  brokerClientId: number;
  brokerReference: string;
  observations: string | null;
  drivers: ReservationDriverDetailsModel[];
  valueItems: ReservationFullCreateValueItemModel[];
}

export interface ReservationFullCreateValueItemModel {
  code: string;
  isAccepted: boolean;
}

export interface ReservationCreateResultModel {
  uid: string;
  number: string;
}

export interface ReservationFilter {
  reservationNumber?: string | null;
  rentalAgreementNumber?: string | null;
  reference?: string | null;
  driverFirstName?: string | null;
  driverLastName?: string | null;
  clientId?: number | null;
  pickupLocations?: string | null;
  dropOffLocations?: string | null;
  dateOutFrom?: string | null;
  dateOutTo?: string | null;
  dateInFrom?: string | null;
  dateInTo?: string | null;
  creationDateFrom?: string | null;
  creationDateTo?: string | null;
  statuses?: ReservationStatus[] | null;
}

export type ReservationVersion = number | 'latest';
